import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, NavigationEnd, Router, RouterStateSnapshot } from '@angular/router';
import { authManagementService } from '@aviv-pro-navigation/authentication-core';
import { UserSessionType } from '@aviv-pro-navigation/authentication-core/dist/types/types/msp-user-session';
import { CookieService } from 'ngx-cookie-service';
import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ReferenceService } from './reference.service';
import { WindowService } from './window.service';

@Injectable({
  providedIn: 'root'
})
export class UserService implements CanActivate {
  private userAccessToken = 'userAccessToken';
  private monitoringId = 'MonitoringId';
  private userSessionInfo:   UserSessionType | null = null;
  private previousUrl: string | null = null;
  constructor(
    private cookieService: CookieService,
    private referenceService: ReferenceService,
    private router: Router,
    private windowService: WindowService
  ) {
    authManagementService.userSession.subscribe(data => {
      if (data) {
        this.userSessionInfo = data;
        return;
      }
      this.userSessionInfo = null;
    });

    this.router.events.subscribe(event => {
      const eventNavigationEnd = event as NavigationEnd;
      if (eventNavigationEnd && eventNavigationEnd.url) {
        this.previousUrl = eventNavigationEnd.url;
        return;
      }
    });
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
    if (!this.isLoggedIn() && !this.isMonitoringMode()) {
      return from(authManagementService.checkSession()).pipe(
        switchMap(() => of(true)),
        catchError(() => {
          this.logout().subscribe({error: logoutError => console.error('Logout failed', logoutError)});
          return throwError('Unauthorized error');
        })
      );
    }
    return this.isLoggedIn() || this.isMonitoringMode();
  }

  getAccessToken(): string {
    return this.cookieService.get(this.userAccessToken);
  }

  get BomsRights(): null | string {
    return this.userSessionInfo?.bomsRights ?? null;
  }

  get IdTiers(): number | null {
    return this.userSessionInfo?.thirdPartyId ?? null;
  }

  get RcuId(): string | null {
    return this.userSessionInfo?.rcuId ?? null;
  }

  isLoggedIn(): boolean {
    return (this.userSessionInfo?.userId ?? 0) > 0;
  }

  isMonitoringMode(): boolean {
    return this.cookieService.check(this.monitoringId);
  }

  logout() {
    return from(authManagementService.signOut()).pipe(
      switchMap(() => this.logoutSuccess()),
      catchError(() => this.logoutSuccess())
    );
  }

  logoutSuccess() {
    let redirectUrl = this.router.url;

    if (redirectUrl === '/' && this.previousUrl !== null) {
      redirectUrl = this.previousUrl;
    }

    const url = [this.referenceService.staticReferences.SsoLogout,
              '?redirectTo=3',
              encodeURIComponent(redirectUrl)].join('');

    this.windowService.nativeWindow.location.href = url;
    return EMPTY;
  }
}
